<template>
  <AnalyticsWidget
    title="Glass Sales Summary"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <template v-slot:title>
      <ReportFilterPopover
        class="float-right mr-2"
        headerTitle="Report Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        @apply="applySummaryFilter"
        @reset="resetSummaryFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Range">
            <ReportDateRangeSelect 
                :startDate="editingSummaryFilter.startDate"
                :endDate="editingSummaryFilter.endDate"
                @updateStartDate="editingSummaryFilter.startDate = $event"
                @updateEndDate="editingSummaryFilter.endDate = $event"
              />
          </ReportFilterGroup>

          <ReportFilterGroup label="Contact">
            <ReportContactSelect
              v-model="editingSummaryFilter.contactId"
              @updateName="editingSummaryFilter.contactName = $event"
            />
          </ReportFilterGroup>

          <ReportFilterGroup label="Price Group Filter">
            <FormMultiSelect v-model="editingSummaryFilter.priceGroupList" :options="priceGroupLookup" />
          </ReportFilterGroup>

          <ReportFilterGroup label="Service Range Filter">
            <FormMultiSelect v-model="editingSummaryFilter.serviceRangeList" :options="serviceRangeLookup" />
          </ReportFilterGroup>
          
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
          </div>
          <div v-if="appliedSummaryFilter.priceGroupList">
            <i class="far fa-money-check-edit-alt mr-2"></i>{{appliedSummaryFilter.priceGroupList.length}} Price Group{{displayCountPlural(appliedSummaryFilter.priceGroupList.length)}}
          </div>
          <div v-if="appliedSummaryFilter.serviceRangeList">
                  <i class="far fa-tools mr-2"></i>{{appliedSummaryFilter.serviceRangeList.length}} Service Range{{displayCountPlural(appliedSummaryFilter.serviceRangeList.length)}}
                </div>
          <div v-if="appliedSummaryFilter.stockList && appliedSummaryFilter.stockList.length > 0">
            <i class="far fa-inventory mr-2"></i>{{appliedSummaryFilter.stockList.length}} Stock{{displayCountPlural(appliedSummaryFilter.stockList.length)}}
          </div>
          <div v-if="appliedSummaryFilter.contactId">
            <i class="far fa-user-circle mr-2"></i>{{appliedSummaryFilter.contactName}}
          </div>
        </template>
      </ReportFilterPopover>
    </template>
    <template v-slot:subtitle>
      Displays detailed sales data for service items sold during a date range<br />
      Based on completed orders and completed order dates. All prices ex-GST
    </template>
    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th> </th>
          <th> </th>
          <th :colspan="totalGroupCount" class="text-center table-data-alternate-column">Price Group Breakdown</th>
        </tr>
        <tr>
          <th> </th>
          <th> </th>
          <th class="table-data-alternate-column" v-for="priceGroup of displayPriceGroupList" :key="priceGroup.id" :colspan="serviceRangesForPriceGroup(priceGroup).length">{{priceGroup.name}}</th>
        </tr>
        <tr>
          <th>Stock</th>
          <th>Total</th>
          <template v-for="priceGroup of displayPriceGroupList">
            <template v-for="serviceRange of serviceRangesForPriceGroup(priceGroup)">
              <th class="table-data-alternate-column" :key="`pg-${priceGroup.id}-sr-${serviceRange.id}`">{{serviceRange.name}}</th>
            </template>
          </template>
        </tr>
      </template>
      <template v-slot:body>
        <template v-for="stock of reportData.stocks">
          <tr class="static-datatable-striped" :key="`main-${stock.id}`">
            <td class="font-weight-bold"><LinkStock :id="stock.id" :name="stock.name" /></td>
            <td class="font-weight-bold">{{formatCurrency(stock.totalSales)}}</td>
            <template v-for="priceGroupData of stock.priceGroupList">
              <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                <td class="font-weight-bold" :key="`${priceGroupData.id}-${serviceRangeData.id}`">{{formatCurrency(serviceRangeData.totalSales)}}</td>
              </template>
            </template>
          </tr>
          <tr class="table-row-sm" :key="`units-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>{{formatLookupName(stock.uomType, UOMTypeLookup)}}</td>
            <td><UOMValue :value="stock.totalUnits" :uomType="stock.uomType" /></td>
            <template v-for="priceGroupData of stock.priceGroupList">
              <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                <td :key="`${priceGroupData.id}-${serviceRangeData.id}`">{{formatDecimal(serviceRangeData.totalUnits, 5)}} <span v-html="formatLookupName(stock.uomType, UOMTypeUnitsLookup)" /></td>
              </template>
            </template>
          </tr>
          <tr class="table-row-sm" :key="`avg-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Avg Sale Price / <span v-html="formatLookupName(stock.uomType, UOMTypeUnitsLookup)" /></td>
            <td>{{formatCurrency(stock.avgSalePrice)}}</td>
            <template v-for="priceGroupData of stock.priceGroupList">
              <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                <td :key="`${priceGroupData.id}-${serviceRangeData.id}`">{{formatCurrency(serviceRangeData.avgSalePrice)}}</td>
              </template>
            </template>
          </tr>
          <tr class="table-row-sm" :key="`spacer-${stock.id}`">
            <td :colspan="totalGroupCount + 2"> </td>
          </tr>
        </template>
      </template>
    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { InventoryStockTypes, UOMTypeLookup, UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'InventoryStockGlassServiceSalesBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    ReportContactSelect
  },
  props: {
    filter: Object,
    stockId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  data() {
    return {
      loading: false,
      loadingError: '',

      defaultSummaryFilter: {    
        startDate: moment().local().subtract(30, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        contactId: null,
        contactName: '',
        priceGroupList: [],
        serviceRangeList: [],
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},

      priceGroupList: [],
      priceGroupLookup: [],
      serviceRangeList: [],
      serviceRangeLookup: [],
      reportData: {
        stockPriceGroups: [],
        stocks: [],
        totals: {}
      }      
    }
  },
  computed: {
    displayPriceGroupList() {
      return this.priceGroupList.filter(priceGroup => {
        if(this.appliedSummaryFilter.priceGroupList.indexOf(priceGroup.id) == -1)
          return false

        // Hide if no service ranges
        return this.serviceRangesForPriceGroup(priceGroup).length > 0
      })
    },
    totalGroupCount() {
      return this.displayPriceGroupList.reduce((curValue, priceGroup) => {
        let serviceRangeList = this.serviceRangesForPriceGroup(priceGroup)
        return curValue + serviceRangeList.length
      }, 0)
    }
  },
  watch: {
    stockId: function() {
      this.requestReportData()
    }    
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.UOMTypeLookup = UOMTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.loading = true
    this.requestData()
  },
  methods: {
    requestData() {
      // Get config data
      Promise.all([
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.pricingService.getServicePriceRangeList()
      ])
      .then(results => {

        this.priceGroupList = results[0].data.filter(priceGroup => priceGroup.isActive)
        this.priceGroupLookup = this.priceGroupList.map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
        this.serviceRangeList = results[1].data.filter(serviceRange => serviceRange.isActive)
        this.serviceRangeLookup = this.serviceRangeList.map(serviceRange => {
          return {
            value: serviceRange.id,
            name: serviceRange.name
          }
        })

        // Default to all price groups selected
        this.defaultSummaryFilter.priceGroupList = this.priceGroupList.map(priceGroup => priceGroup.id)
        this.defaultSummaryFilter.serviceRangeList = this.serviceRangeList.map(serviceRange => serviceRange.id)
        this.resetSummaryFilter()
      })
    },
    requestReportData() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId,
        stockIdList: [this.stockId]
      }

      this.loading = true
      this.loadingError = ''
      this.$apiService.analyticsService.getOrdersValueServicesDetailedList(params)
      .then(response => {
        this.reportData = response.data
      })    
      .catch(err => {
        console.error(err)
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    exportDataCSV() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId,
        stockIdList: [this.stockId],
        format: 'csv'
      }

      let queryString = buildUrl({ queryParams: params })

      this.$apiService.reportingService.generateReportSignedURL(`/intelligence/orders/ordervalue/detailedlist/services${queryString}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    serviceRangesForPriceGroup(priceGroup) {
      return priceGroup.servicePriceRanges.filter(serviceRange => {
        if(!serviceRange.isActive)
          return false

        return (this.appliedSummaryFilter.serviceRangeList.indexOf(serviceRange.id) != -1)
      })
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestReportData()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestReportData()
    }
  }
}
</script>